import { Menu, MenusApi } from '@services/api/menus'
import { GetStaticProps } from 'next'
import React, { useEffect } from 'react'

import { HeadMetadata } from '@components/head-metadata'
import { LeaderBrandSection } from '@components/home/leader-brand'
import { LayoutHome } from '@components/layout-home'
import { FAQCalculadoraSolarSection } from '@components/v2/FAQCalculadoraSolarSection'
import { MediaSection } from '@components/v2/MediaSection'
import { SimulationBanner } from '@components/v2/SimulationBanner'
import { useWindowDimensions } from '@hooks/use-window-dimensions'
import { BlockCallsApi } from '@services/api/block-calls'
import { CalculatorSolarProvider } from 'context/CalculatorSolarContext'
import Image from 'next/image'
import { BlockCall } from 'types/block-call'
import { ExperienceSection } from '@components/pages/calculadora-solar/experience-section'

const SimulationPage: React.FC<{
  main: Menu[]
  footer1: Menu[]
  footer2: Menu[]
  media: BlockCall[]
  projects: BlockCall[]
}> = ({ main, footer1, footer2, media, projects }) => {
  const { width } = useWindowDimensions()

  useEffect(() => {
    document.body.style.overflow = null
  }, [])

  return (
    <LayoutHome mainMenus={main} footerMenus1={footer1} footerMenus2={footer2}>
      <HeadMetadata
        title="Calculadora Solar: Faça Agora a Sua Simulação Online"
        metaTitle="Calculadora Solar: Faça Agora a Sua Simulação Online"
        removeSuffix
        metaDescription="Utilize a nossa calculadora solar e faça a simulação de quanto custa para instalar energia solar, além de conferir mais detalhes sobre o tema!"
      />

      <CalculatorSolarProvider>
        <SimulationBanner
          windowWidth={width}
          title={
            <p
              className="
                mx-auto max-w-[22ch]
                px-4
                pt-10 sm:pt-28 md:pt-0
                text-[28px] leading-[34px]
                sm:text-[32px] sm:leading-[38px]
                md:text-[40px] md:leading-[46px]
                lg:text-[44px] lg:leading-[52px]
                xl:text-[50px] xl:leading-[60px]
                font-normal tracking-wide text-white
              "
            >
              Economize na{' '}
              <span className="font-bold text-orange">conta de luz</span>{' '}
              <span className="block md:inline">com a marca líder</span>{' '}
              <span className="block md:inline">do mercado</span>
            </p>
          }
          logo={
            <Image
              src="/assets/logo-original-white.svg"
              alt="Portal Solar - A marca líder em energia solar."
              width={207}
              height={59}
              className="
                block
                w-[120px] h-[43px]
                sm:w-[133px] sm:h-[48px]
                md:w-[179px] md:h-[65px]
                lg:w-[209px] lg:h-[76px]
                mt-2.5 md:mt-0
              "
              priority
            />
          }
          headingLevel="p"
          imageCropCenter={true}
        />
      </CalculatorSolarProvider>

      {/* INÍCIO SEÇÃO A MARCA LÍDER */}
      <LeaderBrandSection />
      {/* FINAL SEÇÃO A MARCA LÍDER */}

      {/* INÍCIO SEÇÃO EXPERIÊNCIA E QUALIDADE */}
      {projects.length > 0 && (
        <section id="experience-section">
          <ExperienceSection data={projects} windowWidth={width} />
        </section>
      )}
      {/* FINAL SEÇÃO EXPERIÊNCIA E QUALIDADE */}

      {/* INÍCIO SEÇÃO PORTAL NA MÍDIA */}
      <MediaSection data={media} />
      {/* FINAL SEÇÃO PORTAL NA MÍDIA */}

      {/* INÍCIO SEÇÃO FAQ */}
      <FAQCalculadoraSolarSection />
      {/* FINAL SEÇÃO FAQ */}
    </LayoutHome>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    const menus = await MenusApi.all()

    const results = await Promise.allSettled([
      BlockCallsApi.index({ block_type: 'expansion_lp_article' }),
      BlockCallsApi.index({ block_type: 'home_solar_projects' })
    ])

    const fulfilledProjects = results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value.data.blockCalls)
      .flat()

    const rejecteds = results
      .filter(result => result.status === 'rejected')
      .map(result => result.reason)
    console.error({ rejecteds })

    const projects = fulfilledProjects?.filter(
      project => project.blockType === 'home_solar_projects'
    )
    const media = fulfilledProjects?.filter(
      project => project.blockType === 'expansion_lp_article'
    )

    return {
      props: {
        ...menus,
        projects,
        media
      },
      revalidate: 60 * 1
    }
  } catch (error) {
    return {
      notFound: true,
      revalidate: 1
    }
  }
}

export default SimulationPage
